import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Paper,
  Stack,
} from "@mui/material";
import Keypad from "./Keypad";

export default function PackDialog({ open, onClose, product, onConfirm }) {
  const [focused, setFocused] = useState("total");
  const [total, setTotal] = useState("");     // รวม (string เพื่อพิมพ์ลื่น)
  const [deduct, setDeduct] = useState("");   // หัก (string)
  const [inputValue, setInputValue] = useState(""); // ค่าที่พิมพ์บน keypad (ยังไม่ commit)
  const [category, setCategory] = useState("");

  // ---------- Helpers ----------
  const resetForm = () => {
    setCategory("");
    setTotal("");
    setDeduct("");
    setInputValue("");
    setFocused("total");
  };

  // รีเซ็ตเมื่อ dialog ถูกปิดจากภายนอก
  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  // sync จอ Keypad ให้ตรงกับช่องที่โฟกัส
  useEffect(() => {
    setInputValue(focused === "deduct" ? (deduct || "") : (total || ""));
  }, [focused, total, deduct]);

  const netNumber = useMemo(() => {
    const t = parseFloat(total || "0");
    const d = parseFloat(deduct || "0");
    const n = t - d;
    return isNaN(n) ? 0 : n;
  }, [total, deduct]);

  const isNetNegative = netNumber < 0;

  const display2 = (val) =>
    val === "" || val == null ? "0.00" : Number(val).toFixed(2);

  function sanitizeNumber(s) {
    if (s == null) return "";
    let x = String(s).replace(/[^\d.]/g, "");
    const parts = x.split(".");
    if (parts.length > 2) x = parts[0] + "." + parts.slice(1).join("");
    if (/^0+\d/.test(x)) x = x.replace(/^0+/, ""); // กัน 0 นำหน้า (เว้น 0.xx)
    return x;
  }

  // ต้องเลือกประเภท + มีอย่างน้อย 1 ช่อง + สุทธิไม่ติดลบ
  const canConfirm = Boolean(category) && (total !== "" || deduct !== "") && !isNetNegative;

  const handleOk = () => {
    onConfirm?.({
      productId: product?.id,
      productName: product?.name || product?.title || "",
      category,
      total: Number(total || 0),
      deduct: Number(deduct || 0),
      net: Number(netNumber),
    });
    resetForm(); // รีเซ็ตหลังบันทึก
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>เพิ่มสินค้าแพ็ค</DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* ซ้าย: ฟอร์ม */}
          <Grid item xs={12} md={6} lg={8}>
            <Stack spacing={2} sx={{ pr: { md: 2 } }}>
              {product && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    สินค้าที่เลือก
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {product?.name || product?.title || `#${product?.id}`}
                  </Typography>
                </Box>
              )}

              {/* ประเภท */}
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
                  ประเภท
                </Typography>
                <Select
                  value={category}
                  displayEmpty
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <MenuItem value="">
                    <em>เลือกประเภท</em>
                  </MenuItem>
                  <MenuItem value="A">ประเภท A</MenuItem>
                  <MenuItem value="B">ประเภท B</MenuItem>
                  <MenuItem value="C">ประเภท C</MenuItem>
                </Select>
              </FormControl>

              {/* รวม */}
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
                  รวม
                </Typography>
                <TextField
                  fullWidth
                  value={display2(total)}
                  onFocus={() => setFocused("total")}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontWeight: 700,
                      fontSize: 24,
                      bgcolor: focused === "total" ? "action.hover" : "background.paper",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography color="text.secondary">กก.</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* หัก */}
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
                  หัก
                </Typography>
                <TextField
                  fullWidth
                  value={display2(deduct)}
                  onFocus={() => setFocused("deduct")}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontWeight: 700,
                      fontSize: 24,
                      bgcolor: focused === "deduct" ? "action.hover" : "background.paper",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography color="text.secondary">กก.</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* สุทธิ (ห้ามติดลบ) */}
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
                  สุทธิ
                </Typography>
                <TextField
                  fullWidth
                  value={display2(netNumber)}
                  InputProps={{
                    readOnly: true,
                    sx: {
                      fontWeight: 700,
                      fontSize: 24,
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography color="text.secondary">กก.</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    // เปลี่ยนสีตัวเลขเป็นแดงเมื่อสุทธิ < 0
                    "& .MuiInputBase-input": {
                      color: isNetNegative ? "error.main" : "text.primary",
                    },
                  }}
                  helperText={isNetNegative ? "สุทธิห้ามติดลบ" : " "}
                  FormHelperTextProps={{
                    sx: { color: isNetNegative ? "error.main" : "text.secondary" },
                  }}
                />
              </Box>
            </Stack>
          </Grid>

          {/* ขวา: Keypad */}
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <Paper elevation={0}>
              <Keypad
                value={inputValue}
                onChange={(val) => {
                  const next = sanitizeNumber(String(val ?? ""));
                  setInputValue(next);
                }}
                onClear={() => setInputValue("")}
                onSubmit={(result) => {
                  const committed = sanitizeNumber(String(result ?? ""));
                  if (focused === "deduct") {
                    setDeduct(committed);
                  } else {
                    setTotal(committed);
                    setFocused("deduct"); // เสร็จรวม → ย้ายไปกรอก "หัก"
                  }
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      {/* ปุ่มยืนยัน */}
      <DialogActions sx={{ p: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleOk}
          disabled={!canConfirm}
          sx={{
            borderRadius: 0,
            py: 1.8,
            fontWeight: 700,
            bgcolor: "#1E2630",
            "&:hover": { bgcolor: "#111820" },
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
